import { useState, useCallback } from "react";
import type { DateRangePreset } from "../../types/analytics";
import styles from "./DateRangePicker.module.css";

interface DateRangePickerProps {
  from: Date;
  to: Date;
  onChange: (from: Date, to: Date) => void;
}

const PRESETS: { value: DateRangePreset; label: string; days: number }[] = [
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "90d", label: "90 days", days: 90 },
  { value: "1y", label: "1 year", days: 365 },
];

function getPresetFromRange(from: Date, to: Date): DateRangePreset {
  const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
  const preset = PRESETS.find((p) => Math.abs(p.days - diffDays) <= 1);
  return preset?.value ?? "custom";
}

export function DateRangePicker({ from, to, onChange }: DateRangePickerProps) {
  const [active, setActive] = useState<DateRangePreset>(() => getPresetFromRange(from, to));

  const handlePreset = useCallback(
    (preset: (typeof PRESETS)[number]) => {
      const now = new Date();
      const start = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
      setActive(preset.value);
      onChange(start, now);
    },
    [onChange],
  );

  const handleCustomFrom = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = new Date(e.target.value);
      if (!isNaN(d.getTime())) {
        setActive("custom");
        onChange(d, to);
      }
    },
    [to, onChange],
  );

  const handleCustomTo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = new Date(e.target.value);
      if (!isNaN(d.getTime())) {
        setActive("custom");
        onChange(from, d);
      }
    },
    [from, onChange],
  );

  return (
    <div className={styles.container}>
      <div className={styles.presets}>
        {PRESETS.map((p) => (
          <button
            key={p.value}
            className={`${styles.preset} ${active === p.value ? styles.active : ""}`}
            onClick={() => handlePreset(p)}
            type="button"
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className={styles.custom}>
        <input
          type="date"
          value={from.toISOString().slice(0, 10)}
          onChange={handleCustomFrom}
          className={styles.dateInput}
        />
        <span className={styles.separator}>–</span>
        <input
          type="date"
          value={to.toISOString().slice(0, 10)}
          onChange={handleCustomTo}
          className={styles.dateInput}
        />
      </div>
    </div>
  );
}
